import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";
import { languages } from "./i18n/translations";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import { 
  Globe, 
  Video, 
  Users, 
  FileText, 
  CheckCircle, 
  Clock, 
  Shield, 
  Award,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  Menu,
  X,
  Loader2
} from "lucide-react";
import { CGVPage, MentionsLegalesPage, PrivacyPolicyPage } from "./components/LegalPages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language Switcher Component
const LanguageSwitcher = () => {
  const { language, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const currentLang = languages.find(l => l.code === language);

  return (
    <div className="relative">
      <button
        data-testid="language-switcher"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-100 transition-colors"
      >
        <span className="text-lg leading-none">{currentLang?.flag}</span>
        <span className="text-sm font-medium text-slate-700">
          {currentLang?.name}
        </span>
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              data-testid={`lang-${lang.code}`}
              onClick={() => {
                changeLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${
                language === lang.code ? "bg-amber-50 text-amber-700" : "text-slate-700"
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: t("nav.home") },
    { path: "/services", label: t("nav.services") },
    { path: "/about", label: t("nav.about") },
    { path: "/contact", label: t("nav.contact") },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-white/80 backdrop-blur-sm"
      } border-b border-slate-100`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <span className="text-xl font-serif font-semibold text-slate-900">Sasmithe</span>
            <span className="text-amber-600 font-serif">Traductions</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.replace("/", "") || "home"}`}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? "text-amber-600"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/booking">
              <Button 
                data-testid="nav-booking-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white px-6"
              >
                {t("nav.booking")}
              </Button>
            </Link>
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-md hover:bg-slate-100"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-md text-sm font-medium ${
                  location.pathname === link.path
                    ? "bg-amber-50 text-amber-700"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/booking" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full mt-2 bg-slate-900 hover:bg-slate-800 text-white">
                {t("nav.booking")}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

// Footer Component
const Footer = () => {
  const { t, language } = useLanguage();

  const legalLinks = {
    en: { cgv: "Terms & Conditions", mentions: "Legal Notice", privacy: "Privacy Policy" },
    fr: { cgv: "CGV", mentions: "Mentions Légales", privacy: "Politique de Confidentialité" },
    si: { cgv: "නියමයන්", mentions: "නීතිමය දැනුම්දීම", privacy: "රහස්‍යතා ප්‍රතිපත්තිය" },
  };

  const legal = legalLinks[language] || legalLinks.en;

  return (
    <footer className="bg-slate-900 text-white pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 pb-12 border-b border-slate-700">
          {/* Brand */}
          <div>
            <h3 className="font-serif text-2xl font-semibold mb-4">
              Sasmithe <span className="text-amber-500">Traductions</span>
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">
              {t("footer.quickLinks")}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/services" className="text-slate-300 hover:text-amber-500 text-sm transition-colors">
                  {t("nav.services")}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-slate-300 hover:text-amber-500 text-sm transition-colors">
                  {t("nav.about")}
                </Link>
              </li>
              <li>
                <Link to="/booking" className="text-slate-300 hover:text-amber-500 text-sm transition-colors">
                  {t("nav.booking")}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-300 hover:text-amber-500 text-sm transition-colors">
                  {t("nav.contact")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">
              Legal
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/cgv" className="text-slate-300 hover:text-amber-500 text-sm transition-colors">
                  {legal.cgv}
                </Link>
              </li>
              <li>
                <Link to="/mentions-legales" className="text-slate-300 hover:text-amber-500 text-sm transition-colors">
                  {legal.mentions}
                </Link>
              </li>
              <li>
                <Link to="/politique-confidentialite" className="text-slate-300 hover:text-amber-500 text-sm transition-colors">
                  {legal.privacy}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">
              {t("footer.contact")}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <Mail className="w-4 h-4 text-amber-500" />
                sasmitha@hotmail.fr
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Sasmithe Traductions. {t("footer.rights")}.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const HomePage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/20170126/pexels-photo-20170126.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Paris street"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <p className="text-sm font-medium text-amber-600 uppercase tracking-widest mb-4">
              {t("hero.tagline")}
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-semibold text-slate-900 leading-tight mb-6">
              {t("hero.title")}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-10 leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                data-testid="hero-cta-btn"
                onClick={() => navigate("/booking")}
                className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-base shadow-lg"
              >
                {t("hero.cta")}
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                data-testid="hero-learn-more-btn"
                variant="outline"
                onClick={() => navigate("/about")}
                className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-base"
              >
                {t("hero.learnMore")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-stone-50" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-slate-900 mb-4">
              {t("services.title")}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t("services.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Video Interpreting */}
            <Card className="bg-white border-slate-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Business meeting"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardHeader>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <Video className="w-6 h-6 text-amber-600" />
                </div>
                <CardTitle className="font-serif text-xl">{t("services.videoInterpreting")}</CardTitle>
                <CardDescription className="text-slate-600">
                  {t("services.videoDesc")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-slate-900">
                  €75 <span className="text-sm font-normal text-slate-500">{t("services.perHour")}</span>
                </p>
              </CardContent>
            </Card>

            {/* In-Person - Featured */}
            <Card className="bg-white border-t-2 border-t-amber-500 border-slate-100 shadow-lg overflow-hidden md:scale-105">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/7841406/pexels-photo-7841406.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Legal documents"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardHeader>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
                <CardTitle className="font-serif text-xl">{t("services.inPerson")}</CardTitle>
                <CardDescription className="text-slate-600">
                  {t("services.inPersonDesc")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-slate-900">
                  €250 <span className="text-sm font-normal text-slate-500">{t("services.perHalfDay")}</span>
                </p>
              </CardContent>
            </Card>

            {/* Document Translation */}
            <Card className="bg-white border-slate-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1658305808448-a799614b0941?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Sri Lanka"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardHeader>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-amber-600" />
                </div>
                <CardTitle className="font-serif text-xl">{t("services.documentTranslation")}</CardTitle>
                <CardDescription className="text-slate-600">
                  {t("services.documentDesc")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-slate-900">
                  €50 <span className="text-sm font-normal text-slate-500">{t("services.perDocument")}</span>
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button
              data-testid="view-all-services-btn"
              onClick={() => navigate("/services")}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8"
            >
              {t("hero.learnMore")}
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/30535627/pexels-photo-30535627.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional translator"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div>
              <h2 className="font-serif text-4xl md:text-5xl font-medium text-slate-900 mb-6">
                {t("about.whyChoose")}
              </h2>
              <div className="space-y-6">
                {[
                  { icon: Award, text: t("about.reasons.certified") },
                  { icon: Globe, text: t("about.reasons.native") },
                  { icon: CheckCircle, text: t("about.reasons.fluent") },
                  { icon: Shield, text: t("about.reasons.confidential") },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-amber-600" />
                    </div>
                    <p className="text-lg text-slate-700">{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10 p-6 bg-slate-900 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="text-5xl font-serif font-bold text-amber-500">7</div>
                  <div>
                    <p className="text-white font-medium">{t("about.experience")}</p>
                    <p className="text-slate-400 text-sm">French ↔ Sinhala</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-white mb-6">
            {t("booking.title")}
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            {t("booking.subtitle")}
          </p>
          <Button
            data-testid="cta-booking-btn"
            onClick={() => navigate("/booking")}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-10 py-6 text-lg shadow-lg shadow-amber-500/25"
          >
            {t("nav.booking")}
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </main>
  );
};

// Services Page
const ServicesPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data.services);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const langKey = language === "si" ? "si" : language === "fr" ? "fr" : "en";

  return (
    <main className="pt-24 pb-16" data-testid="services-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="font-serif text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            {t("services.title")}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t("services.subtitle")}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(services).map(([id, service]) => (
              <Card key={id} className="bg-white border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <CardTitle className="font-serif text-xl text-slate-900">
                    {service.name[langKey]}
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    {service.description[langKey]}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <p className="text-3xl font-semibold text-slate-900">
                      €{service.price.toFixed(2)}
                    </p>
                    <Button
                      data-testid={`book-service-${id}`}
                      onClick={() => navigate(`/booking?service=${id}`)}
                      className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                    >
                      {t("services.bookNow")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

// About Page
const AboutPage = () => {
  const { t } = useLanguage();

  const competencies = [
    { key: "legal", icon: FileText },
    { key: "business", icon: Users },
    { key: "medical", icon: Shield },
    { key: "cultural", icon: Globe },
  ];

  return (
    <main className="pt-24 pb-16" data-testid="about-page">
      {/* Hero */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="font-serif text-4xl md:text-5xl font-medium text-slate-900 mb-6">
                {t("about.title")}
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                {t("about.subtitle")}
              </p>
              <div className="p-6 bg-slate-900 rounded-lg inline-flex items-center gap-4">
                <div className="text-5xl font-serif font-bold text-amber-500">7</div>
                <div>
                  <p className="text-white font-medium">{t("about.experience")}</p>
                  <p className="text-slate-400 text-sm">French ↔ Sinhala</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/30535627/pexels-photo-30535627.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional translator"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Competencies */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-slate-900 text-center mb-16">
            {t("about.expertise")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {competencies.map(({ key, icon: Icon }) => (
              <Card key={key} className="bg-white border-slate-100 shadow-sm hover:shadow-lg transition-all">
                <CardHeader>
                  <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-amber-600" />
                  </div>
                  <CardTitle className="font-serif text-xl">
                    {t(`about.competencies.${key}`)}
                  </CardTitle>
                  <CardDescription className="text-slate-600 text-base">
                    {t(`about.competencies.${key}Desc`)}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-white text-center mb-16">
            {t("about.whyChoose")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Award, text: t("about.reasons.certified") },
              { icon: Globe, text: t("about.reasons.native") },
              { icon: CheckCircle, text: t("about.reasons.fluent") },
              { icon: Shield, text: t("about.reasons.confidential") },
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-slate-900" />
                </div>
                <p className="text-white font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

// Booking Page
const BookingPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get("service");
  const cancelled = searchParams.get("cancelled");

  const [step, setStep] = useState(1);
  const [services, setServices] = useState({});
  const [selectedService, setSelectedService] = useState(preselectedService || "");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const langKey = language === "si" ? "si" : language === "fr" ? "fr" : "en";

  useEffect(() => {
    if (cancelled) {
      toast.error(t("booking.cancelled"));
    }
  }, [cancelled, t]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, datesRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/available-dates`),
        ]);
        setServices(servicesRes.data.services);
        setAvailableDates(datesRes.data.available_dates);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error(t("common.error"));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const timeSlots = [
    { label: `${t("booking.morning")} - 09:00`, value: "09:00" },
    { label: `${t("booking.morning")} - 10:00`, value: "10:00" },
    { label: `${t("booking.morning")} - 11:00`, value: "11:00" },
    { label: `${t("booking.afternoon")} - 14:00`, value: "14:00" },
    { label: `${t("booking.afternoon")} - 15:00`, value: "15:00" },
    { label: `${t("booking.afternoon")} - 16:00`, value: "16:00" },
  ];

  const handleSubmit = async () => {
    if (!selectedService || !selectedDate || !selectedTime || !formData.name || !formData.email || !formData.phone) {
      toast.error(t("common.error"));
      return;
    }

    setSubmitting(true);
    try {
      // Create booking
      const bookingRes = await axios.post(`${API}/bookings`, {
        service_id: selectedService,
        client_name: formData.name,
        client_email: formData.email,
        client_phone: formData.phone,
        booking_date: selectedDate.toISOString().split("T")[0],
        booking_time: selectedTime,
        notes: formData.notes,
        language: langKey,
      });

      // Create checkout session with selected payment method
      const checkoutRes = await axios.post(`${API}/checkout/create-session`, {
        booking_id: bookingRes.data.id,
        origin_url: window.location.origin,
        payment_method: paymentMethod,
      });

      // Redirect to payment
      window.location.href = checkoutRes.data.url;
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(t("common.error"));
      setSubmitting(false);
    }
  };

  const selectedServiceData = services[selectedService];

  const paymentLabels = {
    en: { choosePayment: "Choose Payment Method", card: "Credit Card", paypal: "PayPal" },
    fr: { choosePayment: "Choisir le mode de paiement", card: "Carte bancaire", paypal: "PayPal" },
    si: { choosePayment: "ගෙවීම් ක්‍රමය තෝරන්න", card: "ක්‍රෙඩිට් කාඩ්", paypal: "PayPal" },
  };
  const paymentText = paymentLabels[langKey] || paymentLabels.en;

  if (loading) {
    return (
      <main className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </main>
    );
  }

  return (
    <main className="pt-24 pb-16 bg-stone-50 min-h-screen" data-testid="booking-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl font-medium text-slate-900 mb-4">
            {t("booking.title")}
          </h1>
          <p className="text-lg text-slate-600">
            {t("booking.subtitle")}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  step >= s ? "bg-amber-500 text-slate-900" : "bg-slate-200 text-slate-500"
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 ${step > s ? "bg-amber-500" : "bg-slate-200"}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <Card className="bg-white shadow-lg">
          <CardContent className="p-8">
            {/* Step 1: Select Service */}
            {step === 1 && (
              <div data-testid="booking-step-1">
                <h2 className="font-serif text-2xl font-medium text-slate-900 mb-6">
                  {t("booking.selectService")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(services).map(([id, service]) => (
                    <button
                      key={id}
                      data-testid={`select-service-${id}`}
                      onClick={() => setSelectedService(id)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedService === id
                          ? "border-amber-500 bg-amber-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <p className="font-medium text-slate-900">{service.name[langKey]}</p>
                      <p className="text-sm text-slate-600 mt-1">{service.description[langKey]}</p>
                      <p className="text-lg font-semibold text-amber-600 mt-2">€{service.price.toFixed(2)}</p>
                    </button>
                  ))}
                </div>
                <div className="mt-8 flex justify-end">
                  <Button
                    data-testid="booking-next-step-1"
                    onClick={() => setStep(2)}
                    disabled={!selectedService}
                    className="bg-slate-900 hover:bg-slate-800 text-white px-8"
                  >
                    {t("common.next")}
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Select Date & Time */}
            {step === 2 && (
              <div data-testid="booking-step-2">
                <h2 className="font-serif text-2xl font-medium text-slate-900 mb-6">
                  {t("booking.selectDate")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => {
                        const dateStr = date.toISOString().split("T")[0];
                        return !availableDates.includes(dateStr);
                      }}
                      className="rounded-md border"
                    />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-4">{t("booking.selectTime")}</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {timeSlots.map((slot) => (
                        <button
                          key={slot.value}
                          data-testid={`select-time-${slot.value}`}
                          onClick={() => setSelectedTime(slot.value)}
                          disabled={!selectedDate}
                          className={`p-3 rounded-lg border text-sm transition-all ${
                            selectedTime === slot.value
                              ? "border-amber-500 bg-amber-50 text-amber-700"
                              : "border-slate-200 hover:border-slate-300 disabled:opacity-50"
                          }`}
                        >
                          {slot.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex justify-between">
                  <Button
                    data-testid="booking-back-step-2"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="border-slate-300"
                  >
                    {t("common.back")}
                  </Button>
                  <Button
                    data-testid="booking-next-step-2"
                    onClick={() => setStep(3)}
                    disabled={!selectedDate || !selectedTime}
                    className="bg-slate-900 hover:bg-slate-800 text-white px-8"
                  >
                    {t("common.next")}
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Your Details */}
            {step === 3 && (
              <div data-testid="booking-step-3">
                <h2 className="font-serif text-2xl font-medium text-slate-900 mb-6">
                  {t("booking.yourDetails")}
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t("booking.name")} *
                    </label>
                    <Input
                      data-testid="booking-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t("booking.name")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t("booking.email")} *
                    </label>
                    <Input
                      data-testid="booking-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t("booking.email")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t("booking.phone")} *
                    </label>
                    <Input
                      data-testid="booking-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder={t("booking.phone")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t("booking.notes")}
                    </label>
                    <Textarea
                      data-testid="booking-notes-input"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder={t("booking.notesPlaceholder")}
                      rows={4}
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-8 p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-medium text-slate-900 mb-3">Résumé / Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Service:</span>
                      <span className="font-medium">{selectedServiceData?.name[langKey]}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Date:</span>
                      <span className="font-medium">{selectedDate?.toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Time:</span>
                      <span className="font-medium">{selectedTime}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-slate-200 mt-2">
                      <span className="font-medium text-slate-900">{t("booking.total")}:</span>
                      <span className="font-bold text-xl text-amber-600">
                        €{selectedServiceData?.price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="mt-6">
                  <h3 className="font-medium text-slate-900 mb-3">{paymentText.choosePayment}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      data-testid="payment-stripe"
                      onClick={() => setPaymentMethod("stripe")}
                      className={`p-4 rounded-lg border-2 flex items-center gap-3 transition-all ${
                        paymentMethod === "stripe"
                          ? "border-amber-500 bg-amber-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">S</span>
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-900">{paymentText.card}</p>
                        <p className="text-xs text-slate-500">Visa, Mastercard...</p>
                      </div>
                    </button>
                    <button
                      data-testid="payment-paypal"
                      onClick={() => setPaymentMethod("paypal")}
                      className={`p-4 rounded-lg border-2 flex items-center gap-3 transition-all ${
                        paymentMethod === "paypal"
                          ? "border-amber-500 bg-amber-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="w-10 h-10 bg-[#003087] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">P</span>
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-900">{paymentText.paypal}</p>
                        <p className="text-xs text-slate-500">PayPal</p>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="mt-8 flex justify-between">
                  <Button
                    data-testid="booking-back-step-3"
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="border-slate-300"
                  >
                    {t("common.back")}
                  </Button>
                  <Button
                    data-testid="booking-submit"
                    onClick={handleSubmit}
                    disabled={submitting || !formData.name || !formData.email || !formData.phone}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 px-8"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {t("booking.continue")}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

// Booking Success Page
const BookingSuccessPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const isPaypal = searchParams.get("paypal") === "true";
  const paymentId = searchParams.get("paymentId");
  const payerId = searchParams.get("PayerID");
  const bookingId = searchParams.get("booking_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // Handle PayPal return
    if (isPaypal && paymentId && payerId) {
      const executePayPal = async () => {
        try {
          const response = await axios.post(`${API}/paypal/execute?payment_id=${paymentId}&payer_id=${payerId}`);
          if (response.data.status === "success" || response.data.payment_status === "paid") {
            setStatus("success");
          } else {
            setStatus("error");
          }
        } catch (error) {
          console.error("PayPal execute error:", error);
          setStatus("error");
        }
      };
      executePayPal();
      return;
    }

    // Handle Stripe return
    if (!sessionId && !isPaypal) {
      setStatus("error");
      return;
    }

    if (sessionId) {
      const checkStatus = async () => {
        try {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (response.data.payment_status === "paid") {
            setStatus("success");
          } else if (response.data.status === "expired") {
            setStatus("error");
          } else if (attempts < 5) {
            setTimeout(() => setAttempts(a => a + 1), 2000);
          } else {
            setStatus("pending");
          }
        } catch (error) {
          console.error("Error checking status:", error);
          if (attempts < 5) {
            setTimeout(() => setAttempts(a => a + 1), 2000);
          } else {
            setStatus("error");
          }
        }
      };
      checkStatus();
    }
  }, [sessionId, attempts, isPaypal, paymentId, payerId]);

  return (
    <main className="pt-24 pb-16 min-h-screen bg-stone-50 flex items-center justify-center" data-testid="booking-success-page">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="p-8 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-amber-600 mx-auto mb-4" />
              <p className="text-slate-600">{t("common.loading")}</p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-serif text-2xl font-medium text-slate-900 mb-4">
                {t("booking.success.title")}
              </h1>
              <p className="text-slate-600 mb-8">
                {t("booking.success.message")}
              </p>
              <Button
                data-testid="success-back-home"
                onClick={() => navigate("/")}
                className="bg-slate-900 hover:bg-slate-800 text-white px-8"
              >
                {t("booking.success.backHome")}
              </Button>
            </>
          )}
          {(status === "error" || status === "pending") && (
            <>
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="font-serif text-2xl font-medium text-slate-900 mb-4">
                {status === "error" ? t("common.error") : "Payment Processing"}
              </h1>
              <p className="text-slate-600 mb-8">
                {status === "error" 
                  ? t("common.tryAgain")
                  : "Your payment is being processed. You will receive a confirmation email shortly."}
              </p>
              <Button
                onClick={() => navigate("/")}
                className="bg-slate-900 hover:bg-slate-800 text-white px-8"
              >
                {t("booking.success.backHome")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
};

// Contact Page
const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language: language,
      });
      toast.success(t("contact.success"));
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error(t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-24 pb-16 bg-stone-50 min-h-screen" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="font-serif text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            {t("contact.title")}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t("contact.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="bg-white shadow-lg">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("contact.name")} *
                  </label>
                  <Input
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("contact.email")} *
                  </label>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("contact.subject")} *
                  </label>
                  <Input
                    data-testid="contact-subject-input"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("contact.message")} *
                  </label>
                  <Textarea
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    required
                  />
                </div>
                <Button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("contact.send")}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="space-y-8">
            <Card className="bg-white shadow-lg">
              <CardContent className="p-8">
                <h2 className="font-serif text-2xl font-medium text-slate-900 mb-6">
                  {t("contact.info.title")}
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t("contact.info.email")}</p>
                      <p className="font-medium text-slate-900">contact@sasmithe-traductions.com</p>
                    </div>
                  </div>
                </div>
                <p className="mt-6 text-sm text-slate-600">
                  {t("contact.info.response")}
                </p>
              </CardContent>
            </Card>

            {/* Map placeholder */}
            <Card className="bg-slate-900 text-white overflow-hidden">
              <CardContent className="p-8">
                <h3 className="font-serif text-xl mb-4">Sasmithe Traductions</h3>
                <p className="text-slate-300 text-sm">
                  Professional French-Sinhala Translation Services
                </p>
                <div className="mt-6 flex items-center gap-2 text-amber-500">
                  <Globe className="w-4 h-4" />
                  <span className="text-sm">Available Worldwide</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </main>
  );
};

// Layout wrapper
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <div className="flex-grow">{children}</div>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/booking/success" element={<BookingSuccessPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/cgv" element={<CGVPage />} />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/politique-confidentialite" element={<PrivacyPolicyPage />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
